import { useCallback, useContext, useState } from 'react';
import api from 'api';
import { NDA_STATUSES, NdaStatus } from 'models/Nda';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { NdaStatusUpdateEventData } from 'services/socketService/socketEventData';
import { sentryError } from 'utils';
import { useTranslation } from 'react-i18next';

export type LeadNdaProps = {
  signingStatus: NdaStatus;
  envelopeId?: string;
  manuallySetNDAStatus?: boolean;
  sentDate?: Date;
};

export default function useNdaStatus(
  leadId: string,
  envelopeId: string,
  updateNdaProps?: (leadNdaProps: LeadNdaProps) => void,
) {
  const [isLoading, setIsLoading] = useState(false);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const onSendNda = useCallback(
    async (config?: {
      message?: string;
      shouldChangeSentDate?: boolean;
      onSuccess?: () => void;
    }) => {
      const { message, shouldChangeSentDate = false, onSuccess } = config || {};

      if (!leadId) return;

      try {
        setIsLoading(true);

        const { data } = await api.portfolio.seller.sendEnvelope(
          leadId,
          message,
        );

        updateNdaProps?.({
          signingStatus: NDA_STATUSES.SENT,
          envelopeId: data.envelopeId,
          manuallySetNDAStatus: false,
          ...(shouldChangeSentDate && { sentDate: new Date() }),
        });

        popSuccess(t('successfullysentNDA'));
        onSuccess?.();
      } catch (e) {
        sentryError(e);
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [leadId, popServerError, popSuccess, t, updateNdaProps],
  );

  const sendNda = useCallback(
    (message?: string, onSuccess?: () => void) =>
      onSendNda({ message, shouldChangeSentDate: true, onSuccess }),
    [onSendNda],
  );

  const resendNda = useCallback(
    (onSuccess?: () => void) => onSendNda({ onSuccess }),
    [onSendNda],
  );

  const getAndOpenNdaLink = useCallback(async () => {
    if (!leadId || !envelopeId) return;

    try {
      setIsLoading(true);

      const {
        data: { url },
      } = await api.portfolio.seller.viewNda(leadId, envelopeId);

      window.location.href = url;
    } catch (e) {
      sentryError(e);
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [envelopeId, leadId, popServerError]);

  const newStatus = useCallback(
    ({
      signingStatus,
      envelopeId,
      manuallySetNDAStatus,
      leadId: payloadLeadId,
    }: NdaStatusUpdateEventData) => {
      if (leadId !== payloadLeadId) return;

      updateNdaProps?.({
        signingStatus,
        envelopeId,
        manuallySetNDAStatus,
      });
    },
    [leadId, updateNdaProps],
  );

  useSocketEvent(LISTEN_EVENTS.NDA_STATUS_UPDATE, newStatus);

  return { isLoading, sendNda, resendNda, getAndOpenNdaLink };
}
